import React, { useState, useEffect } from 'react';

const STEPS = [
  { message: 'Unfolding your Section 32/Contract of Sale…', progress: 8 },
  { message: 'Reading the fine print…', progress: 22 },
  { message: 'Sniffing out easements…', progress: 38 },
  { message: 'Hunting hidden fees…', progress: 55 },
  { message: 'Drawing up your plan…', progress: 72 },
  { message: 'Circling your lot…', progress: 86 },
  { message: 'Polishing the report…', progress: 94 },
];

// Once the scripted steps run out (long documents), keep the wait charming.
const IDLE_MESSAGES = [
  'Reading page 62 twice, just to be sure…',
  'Straightening the survey pegs…',
  'Making the circle extra round…',
  'Cross-checking the covenants…',
  'Asking the title very politely…',
];

// A miniature plan of subdivision that sketches itself: streets, three lots,
// a scanning magnifying glass, and the signature red circle on lot 12.
// One shared 8s loop; every element keys its phase off the same timeline.
function PlanSketch() {
  return (
    <div className="plan-card" aria-hidden="true">
      <svg className="plan-svg" width="288" height="228" viewBox="0 0 240 190" overflow="visible">
        <defs>
          <pattern id="s32grid" width="14" height="14" patternUnits="userSpaceOnUse">
            <path d="M14 0H0V14" fill="none" stroke="#DDD5C5" strokeWidth="0.6" opacity="0.55" />
          </pattern>
          <style>{`
            .s32-scene { animation: s32scene 8s linear infinite; }
            @keyframes s32scene {
              0% { opacity: 0; } 3% { opacity: 1; } 92% { opacity: 1; }
              97% { opacity: 0; } 100% { opacity: 0; }
            }
            .s32-draw {
              stroke-dasharray: 1; stroke-dashoffset: 1; fill: none;
              animation: 8s linear infinite;
            }
            @keyframes s32road   { 0%,3%  { stroke-dashoffset: 1; } 10% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; } }
            @keyframes s32lotA   { 0%,10% { stroke-dashoffset: 1; } 18% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; } }
            @keyframes s32lotB   { 0%,16% { stroke-dashoffset: 1; } 24% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; } }
            @keyframes s32lotC   { 0%,22% { stroke-dashoffset: 1; } 30% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; } }
            @keyframes s32dim    { 0%,34% { stroke-dashoffset: 1; opacity: 0; } 35% { opacity: 1; } 38% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; opacity: 1; } }
            @keyframes s32ease   { 0%,38% { stroke-dashoffset: 1; opacity: 0; } 39% { opacity: 1; } 44% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; opacity: 1; } }
            @keyframes s32north  { 0%,4%  { stroke-dashoffset: 1; } 9% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; } }
            @keyframes s32circle { 0%,64% { stroke-dashoffset: 1; } 72% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; } }

            .s32-fade { opacity: 0; animation: 8s linear infinite; }
            @keyframes s32street { 0%,8%  { opacity: 0; } 12% { opacity: 1; } 100% { opacity: 1; } }
            @keyframes s32e1     { 0%,42% { opacity: 0; } 46% { opacity: 1; } 100% { opacity: 1; } }
            @keyframes s32dimtxt { 0%,36% { opacity: 0; } 40% { opacity: 1; } 100% { opacity: 1; } }

            .s32-pop {
              opacity: 0; transform-box: fill-box; transform-origin: center;
              animation: 8s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
            }
            @keyframes s32num11 { 0%,20% { opacity: 0; transform: scale(0); } 24%,100% { opacity: 1; transform: scale(1); } }
            @keyframes s32num12 { 0%,26% { opacity: 0; transform: scale(0); } 30%,100% { opacity: 1; transform: scale(1); } }
            @keyframes s32num13 { 0%,32% { opacity: 0; transform: scale(0); } 36%,100% { opacity: 1; transform: scale(1); } }

            .s32-glass { animation: s32sweep 8s ease-in-out infinite; }
            @keyframes s32sweep {
              0%       { transform: translate(52px, 108px) rotate(-6deg); opacity: 0; }
              8%       { opacity: 0; }
              12%      { transform: translate(52px, 100px) rotate(-6deg); opacity: 1; }
              32%      { transform: translate(193px, 98px) rotate(5deg); }
              48%      { transform: translate(124px, 100px) rotate(-3deg); }
              62%      { transform: translate(124px, 100px) rotate(-3deg); opacity: 1; }
              70%      { transform: translate(150px, 62px) rotate(8deg); opacity: 0; }
              100%     { transform: translate(150px, 62px) rotate(8deg); opacity: 0; }
            }
            .s32-glass-bob { animation: s32bob 2.1s ease-in-out infinite; }
            @keyframes s32bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3.5px); } }

            .s32-wobble { transform-box: fill-box; transform-origin: center; animation: s32wob 8s ease-in-out infinite; }
            @keyframes s32wob {
              0%, 71% { transform: rotate(0deg) scale(1); }
              73.5%   { transform: rotate(-4deg) scale(1.06); }
              76%     { transform: rotate(2.5deg) scale(0.99); }
              78%,100%{ transform: rotate(0deg) scale(1); }
            }
            .s32-spark {
              opacity: 0; transform-box: fill-box; transform-origin: center;
              animation: 8s ease-out infinite;
            }
            @keyframes s32sp1 { 0%,76% { opacity: 0; transform: scale(0) rotate(0deg); } 79% { opacity: 1; transform: scale(1) rotate(18deg); } 83%,100% { opacity: 0; transform: scale(0.2) rotate(30deg); } }
            @keyframes s32sp2 { 0%,79% { opacity: 0; transform: scale(0) rotate(0deg); } 82% { opacity: 1; transform: scale(1) rotate(-14deg); } 86%,100% { opacity: 0; transform: scale(0.2) rotate(-26deg); } }
            @keyframes s32sp3 { 0%,82% { opacity: 0; transform: scale(0) rotate(0deg); } 85% { opacity: 1; transform: scale(0.85) rotate(10deg); } 89%,100% { opacity: 0; transform: scale(0.15) rotate(22deg); } }

            @media (prefers-reduced-motion: reduce) {
              .s32-scene, .s32-draw, .s32-fade, .s32-pop, .s32-glass, .s32-glass-bob, .s32-wobble, .s32-spark {
                animation: none;
              }
              .s32-draw { stroke-dashoffset: 0; }
              .s32-fade, .s32-pop { opacity: 1; transform: none; }
              .s32-glass, .s32-spark { opacity: 0; }
            }
          `}</style>
        </defs>

        {/* graph paper */}
        <rect x="-10" y="-10" width="260" height="210" fill="url(#s32grid)" rx="4" />

        <g className="s32-scene">
          {/* north arrow doodle */}
          <g className="s32-draw" style={{ animationName: 's32north' }}>
            <circle cx="217" cy="24" r="10" pathLength="1" stroke="#7A6248" strokeWidth="1.4"
                    style={{ animationName: 's32north' }} className="s32-draw" />
            <path d="M217 30 L217 18 M213.5 21.5 L217 18 L220.5 21.5" pathLength="1" stroke="#7A6248"
                  strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
                  style={{ animationName: 's32north' }} className="s32-draw" />
          </g>
          <text className="s32-fade" style={{ animationName: 's32street' }} x="224" y="14"
                fontSize="7" fontWeight="700" fill="#7A6248">N</text>

          {/* street */}
          <path d="M6 152 C 70 148, 170 148, 234 153" pathLength="1" className="s32-draw"
                style={{ animationName: 's32road' }} stroke="#4A3220" strokeWidth="2" strokeLinecap="round" />
          <path d="M6 170 C 70 165, 170 165, 234 171" pathLength="1" className="s32-draw"
                style={{ animationName: 's32road' }} stroke="#4A3220" strokeWidth="2" strokeLinecap="round" />
          <text className="s32-fade" style={{ animationName: 's32street' }} x="120" y="164"
                textAnchor="middle" fontSize="7.5" letterSpacing="3.5" fontStyle="italic"
                fontFamily="Georgia, 'Times New Roman', serif" fill="#7A6248">MULBERRY LANE</text>

          {/* lots — slightly wonky on purpose */}
          <polygon points="18,60 88,55 90,144 20,147" pathLength="1" className="s32-draw"
                   style={{ animationName: 's32lotA' }} stroke="#4A3220" strokeWidth="1.8" strokeLinejoin="round" />
          <polygon points="92,55 158,57 156,144 94,144" pathLength="1" className="s32-draw"
                   style={{ animationName: 's32lotB' }} stroke="#4A3220" strokeWidth="1.8" strokeLinejoin="round" />
          <polygon points="162,57 224,53 227,141 160,144" pathLength="1" className="s32-draw"
                   style={{ animationName: 's32lotC' }} stroke="#4A3220" strokeWidth="1.8" strokeLinejoin="round" />

          {/* lot numerals pop in */}
          <text className="s32-pop" style={{ animationName: 's32num11' }} x="53" y="106"
                textAnchor="middle" fontSize="14" fontWeight="700" fill="#4A3220">11</text>
          <text className="s32-pop" style={{ animationName: 's32num12' }} x="124" y="104"
                textAnchor="middle" fontSize="14" fontWeight="700" fill="#4A3220">12</text>
          <text className="s32-pop" style={{ animationName: 's32num13' }} x="193" y="102"
                textAnchor="middle" fontSize="14" fontWeight="700" fill="#4A3220">13</text>

          {/* dimension doodle under lot 12 */}
          <path d="M96 134 L154 134 M96 130.5 L96 137.5 M154 130.5 L154 137.5" pathLength="1"
                className="s32-draw" style={{ animationName: 's32dim' }}
                stroke="#7A6248" strokeWidth="1.1" strokeLinecap="round" />
          <text className="s32-fade" style={{ animationName: 's32dimtxt' }} x="125" y="129"
                textAnchor="middle" fontSize="6.5" fontStyle="italic" fill="#7A6248">18.29</text>

          {/* easement along the rear */}
          <path d="M20 70 C 90 65, 160 65, 225 63" pathLength="1" className="s32-draw"
                style={{ animationName: 's32ease', strokeDasharray: '0.04 0.02' }}
                stroke="#b45309" strokeWidth="1.3" strokeLinecap="round" />
          <text className="s32-fade" style={{ animationName: 's32e1' }} x="30" y="79"
                fontSize="6.5" fontStyle="italic" fill="#b45309">E-1</text>

          {/* the signature red circle on lot 12, with a wobble and sparkles */}
          <g className="s32-wobble">
            <ellipse cx="124" cy="99" rx="17" ry="13.5" pathLength="1" className="s32-draw"
                     style={{ animationName: 's32circle' }}
                     stroke="#E03131" strokeWidth="2.6" transform="rotate(-7 124 99)" />
          </g>
          <path className="s32-spark" style={{ animationName: 's32sp1' }} transform="translate(146 82)"
                d="M0 -5 L1.2 -1.2 L5 0 L1.2 1.2 L0 5 L-1.2 1.2 L-5 0 L-1.2 -1.2 Z" fill="#E03131" opacity="0" />
          <path className="s32-spark" style={{ animationName: 's32sp2' }} transform="translate(103 84) scale(0.75)"
                d="M0 -5 L1.2 -1.2 L5 0 L1.2 1.2 L0 5 L-1.2 1.2 L-5 0 L-1.2 -1.2 Z" fill="#b45309" opacity="0" />
          <path className="s32-spark" style={{ animationName: 's32sp3' }} transform="translate(140 116) scale(0.6)"
                d="M0 -5 L1.2 -1.2 L5 0 L1.2 1.2 L0 5 L-1.2 1.2 L-5 0 L-1.2 -1.2 Z" fill="#E03131" opacity="0" />

          {/* magnifying glass, bobbing as it scans */}
          <g className="s32-glass">
            <g className="s32-glass-bob">
              <circle cx="0" cy="0" r="13" fill="#F5EAD8" fillOpacity="0.45" stroke="#4A3220" strokeWidth="2.6" />
              <path d="M-6 -4 A 7.5 7.5 0 0 1 4 -7" fill="none" stroke="#FFFDF9" strokeWidth="2.2"
                    strokeLinecap="round" opacity="0.9" />
              <line x1="9.5" y1="9.5" x2="21" y2="21" stroke="#4A3220" strokeWidth="4" strokeLinecap="round" />
            </g>
          </g>
        </g>
      </svg>

      {/* washi tape corners */}
      <span className="plan-tape plan-tape-left" />
      <span className="plan-tape plan-tape-right" />
    </div>
  );
}

// Real pipeline stages reported by the server. Each owns a progress window;
// the bar creeps within it so it never looks stalled, and never moves backwards.
const STAGE_META = {
  reading:    { message: () => 'Unfolding your Section 32/Contract of Sale…', from: 6, to: 14 },
  extracting: {
    message: (s) => (s?.pages ? `Reading all ${s.pages} pages…` : 'Reading every page…'),
    from: 15, to: 38,
  },
  analyzing:  { message: () => 'Combing for easements, fees and risks…', from: 40, to: 84 },
  finalizing: { message: () => 'Drawing your plan and double-checking fees…', from: 86, to: 96 },
};

export default function LoadingState({ filename, stage }) {
  const meta = stage ? STAGE_META[stage.key] : null;
  const [pct, setPct] = useState(STEPS[0].progress);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 2750);
    return () => clearInterval(interval);
  }, []);

  // New stage: jump the bar to at least the stage's floor.
  useEffect(() => {
    if (meta) setPct((p) => Math.max(p, meta.from));
  }, [stage?.key]);

  // Creep towards the stage ceiling; without stage info, run the scripted steps.
  useEffect(() => {
    if (meta) {
      setPct((p) => Math.min(p + 1.5, meta.to));
    } else {
      const idx = Math.min(Math.floor(tick / 2), STEPS.length - 1);
      setPct((p) => Math.max(p, STEPS[idx].progress));
    }
  }, [tick]);

  let message;
  if (meta) {
    // The analysis stage is the long one — alternate in the flavor lines.
    message = stage.key === 'analyzing' && tick % 4 >= 2
      ? IDLE_MESSAGES[Math.floor(tick / 4) % IDLE_MESSAGES.length]
      : meta.message(stage);
  } else {
    const idx = Math.min(Math.floor(tick / 2), STEPS.length - 1);
    message = idx < STEPS.length - 1
      ? STEPS[idx].message
      : IDLE_MESSAGES[Math.floor(tick / 2 - STEPS.length + 1) % IDLE_MESSAGES.length];
  }
  const progress = Math.round(pct);

  return (
    <div className="loading-container">
      <PlanSketch />
      <h2>Giving your Section 32/Contract of Sale a proper read</h2>
      <p className="loading-filename">{filename}</p>

      <div className="progress-bar-wrapper">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="progress-percent">{progress}%</span>
      </div>

      <p className="loading-message" key={message}>{message}</p>

      <div className="loading-times">
        <p className="loading-note">Typical analysis times by document size:</p>
        <ul>
          <li><span>Up to 50 pages</span><span>1–2 minutes</span></li>
          <li><span>Around 100 pages</span><span>2–3 minutes</span></li>
          <li><span>150+ pages</span><span>3–4 minutes</span></li>
        </ul>
        <p className="loading-note">Please don't close this page.</p>
      </div>
    </div>
  );
}
